import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Zap, Wind, Droplets, Battery, Grid3X3, HelpCircle,
  MapPin, Calendar, Wrench, FileText, Sparkles, Upload,
  Loader2, AlertTriangle, CheckCircle, Clock, XCircle,
  Download, Plus
} from 'lucide-react';
import { assetsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { StatusBadge, TypeIcon } from './AssetsPage';
import AssetModal from './AssetModal';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const TABS = [
  { id: 'overview',     label: 'Overview',     icon: Zap       },
  { id: 'maintenance',  label: 'Maintenance',  icon: Wrench    },
  { id: 'documents',    label: 'Documents',    icon: FileText  },
  { id: 'ai',           label: 'AI Inspection',icon: Sparkles  },
];

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{value || '—'}</span>
    </div>
  );
}

const MAINT_STATUS = {
  open:        { cls: 'bg-blue-50 text-blue-700',    label: 'Open'        },
  in_progress: { cls: 'bg-amber-50 text-amber-700',  label: 'In Progress' },
  completed:   { cls: 'bg-emerald-50 text-emerald-700', label: 'Completed' },
  cancelled:   { cls: 'bg-gray-50 text-gray-500',    label: 'Cancelled'   },
};

const PRIORITY = {
  low:      'bg-gray-100 text-gray-600',
  medium:   'bg-blue-100 text-blue-700',
  high:     'bg-amber-100 text-amber-700',
  critical: 'bg-red-100 text-red-700',
};

export default function AssetDetailPage() {
  const { id }      = useParams();
  const navigate    = useNavigate();
  const { hasRole } = useAuth();
  const canWrite    = hasRole('super_admin','operations_mgr');

  const [asset, setAsset]         = useState(null);
  const [maintenance, setMaint]   = useState([]);
  const [documents, setDocs]      = useState([]);
  const [aiReport, setAiReport]   = useState('');
  const [loading, setLoading]     = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [editOpen, setEditOpen]   = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadAsset = async () => {
    try {
      const [a, m, d] = await Promise.all([
        assetsAPI.get(id),
        assetsAPI.maintenance(id),
        assetsAPI.docs(id),
      ]);
      setAsset(a.data.data);
      setMaint(m.data.data);
      setDocs(d.data.data);
    } catch { navigate('/assets'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAsset(); }, [id]);

  const handleGenerateReport = async () => {
    setAiLoading(true);
    setActiveTab('ai');
    try {
      const { data } = await assetsAPI.aiInspection(id, {});
      setAiReport(data.data.report);
      toast.success('AI inspection report generated');
    } catch {}
    finally { setAiLoading(false); }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', 'general');
    setUploading(true);
    try {
      await assetsAPI.uploadDoc(id, fd);
      toast.success('Document uploaded');
      const { data } = await assetsAPI.docs(id);
      setDocs(data.data);
    } catch {}
    finally { setUploading(false); e.target.value = ''; }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={24} className="animate-spin text-primary-500" />
    </div>
  );

  if (!asset) return null;

  const warrantyOk = asset.warranty_expiry && new Date(asset.warranty_expiry) > new Date();

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      {/* Back + header */}
      <div>
        <button onClick={() => navigate('/assets')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors">
          <ArrowLeft size={16} /> Back to Assets
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <TypeIcon type={asset.asset_type} />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{asset.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-500">{asset.asset_code}</span>
                <span className="text-gray-300">·</span>
                <StatusBadge status={asset.status} />
                {asset.location_name && <>
                  <span className="text-gray-300">·</span>
                  <span className="text-sm text-gray-500 flex items-center gap-1"><MapPin size={12}/>{asset.location_name}</span>
                </>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleGenerateReport}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors font-medium">
              <Sparkles size={14}/> AI Inspection
            </button>
            {canWrite && (
              <button onClick={() => setEditOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                Edit Asset
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Key stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Capacity', value: asset.capacity_mw ? `${asset.capacity_mw} MW` : '—' },
          { label: 'Installed', value: asset.install_date ? new Date(asset.install_date).toLocaleDateString('en-GB',{month:'short',year:'numeric'}) : '—' },
          { label: 'Warranty', value: asset.warranty_expiry ? new Date(asset.warranty_expiry).toLocaleDateString('en-GB',{month:'short',year:'numeric'}) : '—',
            highlight: warrantyOk ? 'text-emerald-600' : asset.warranty_expiry ? 'text-danger' : '' },
          { label: 'Work Orders', value: maintenance.filter(m=>m.status!=='completed'&&m.status!=='cancelled').length },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{s.label}</p>
            <p className={clsx('text-xl font-bold mt-1', s.highlight || 'text-gray-900')}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={clsx('flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab===t.id ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
              <Icon size={14}/>{t.label}
            </button>
          );
        })}
      </div>

      {/* Tab: Overview */}
      {activeTab==='overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Technical Details</h3>
            <InfoRow label="Asset Type" value={asset.asset_type?.charAt(0).toUpperCase()+asset.asset_type?.slice(1)} />
            <InfoRow label="Manufacturer" value={asset.manufacturer} />
            <InfoRow label="Model Number" value={asset.model_number} />
            <InfoRow label="Serial Number" value={asset.serial_number} />
            <InfoRow label="Capacity" value={asset.capacity_mw ? `${asset.capacity_mw} MW` : null} />
            <InfoRow label="Install Date" value={asset.install_date ? new Date(asset.install_date).toLocaleDateString('en-GB') : null} />
            <InfoRow label="Warranty Expiry" value={asset.warranty_expiry ? new Date(asset.warranty_expiry).toLocaleDateString('en-GB') : null} />
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Location & Financial</h3>
            <InfoRow label="Location" value={asset.location_name} />
            <InfoRow label="Country" value={asset.country} />
            <InfoRow label="Coordinates" value={asset.latitude && asset.longitude ? `${asset.latitude}, ${asset.longitude}` : null} />
            <InfoRow label="Purchase Price" value={asset.purchase_price ? `€${Number(asset.purchase_price).toLocaleString()}` : null} />
            <InfoRow label="Depreciation" value={asset.depreciation_years ? `${asset.depreciation_years} years` : null} />
            <InfoRow label="Salvage Value" value={asset.salvage_value ? `€${Number(asset.salvage_value).toLocaleString()}` : null} />
          </div>
          {asset.notes && (
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{asset.notes}</p>
            </div>
          )}
        </div>
      )}

      {/* Tab: Maintenance */}
      {activeTab==='maintenance' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Maintenance History ({maintenance.length})</h3>
          </div>
          {maintenance.length===0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No maintenance records for this asset.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {maintenance.map(m => (
                <div key={m.id} className="px-5 py-4 hover:bg-surface transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900 text-sm">{m.title}</p>
                        <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium', MAINT_STATUS[m.status]?.cls)}>
                          {MAINT_STATUS[m.status]?.label}
                        </span>
                        <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium capitalize', PRIORITY[m.priority])}>
                          {m.priority}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 capitalize">{m.maintenance_type?.replace('_',' ')} · WO: {m.work_order_number}</p>
                      {m.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{m.description}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      {m.scheduled_date && <p className="text-xs text-gray-500">{new Date(m.scheduled_date).toLocaleDateString('en-GB')}</p>}
                      {m.actual_cost && <p className="text-xs font-medium text-gray-700 mt-0.5">€{Number(m.actual_cost).toLocaleString()}</p>}
                      {m.technician_name && <p className="text-xs text-gray-400 mt-0.5">{m.technician_name}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Documents */}
      {activeTab==='documents' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Documents ({documents.length})</h3>
            {canWrite && (
              <label className={clsx('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors cursor-pointer', uploading && 'opacity-60 pointer-events-none')}>
                {uploading ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>}
                {uploading ? 'Uploading…' : 'Upload'}
                <input type="file" className="hidden" onChange={handleDocUpload} accept=".pdf,.png,.jpg,.jpeg,.docx,.xlsx" />
              </label>
            )}
          </div>
          {documents.length===0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No documents uploaded yet.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {documents.map(d => (
                <div key={d.id} className="px-5 py-3 flex items-center justify-between hover:bg-surface transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary-50 flex items-center justify-center">
                      <FileText size={14} className="text-primary-500"/>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{d.file_name}</p>
                      <p className="text-xs text-gray-400 capitalize">{d.doc_category} · {d.file_size ? `${Math.round(d.file_size/1024)} KB` : ''}</p>
                    </div>
                  </div>
                  <a href={d.file_path} target="_blank" rel="noopener noreferrer"
                    className="p-1.5 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors">
                    <Download size={15}/>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: AI Inspection */}
      {activeTab==='ai' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-primary-500"/>
              <h3 className="text-sm font-semibold text-gray-700">AI Inspection Report</h3>
            </div>
            <button onClick={handleGenerateReport} disabled={aiLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-60 transition-colors">
              {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
              {aiLoading ? 'Generating…' : aiReport ? 'Regenerate' : 'Generate Report'}
            </button>
          </div>
          {aiLoading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
              <Loader2 size={20} className="animate-spin text-primary-500"/>
              <p className="text-sm">AI is analysing asset performance data…</p>
            </div>
          ) : aiReport ? (
            <div className="prose prose-sm max-w-none">
              <div className="bg-primary-50 border border-primary-100 rounded-xl p-5 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {aiReport}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400">
              <Sparkles size={32} className="mx-auto mb-3 text-gray-300"/>
              <p className="text-sm">Click "Generate Report" to get an AI-powered inspection report based on this asset's performance logs and maintenance history.</p>
            </div>
          )}
        </div>
      )}

      {editOpen && <AssetModal asset={asset} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); loadAsset(); }} />}
    </div>
  );
}
